import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatAmount, type AdminOrderDto, type OrderStatus, type PaymentTotalsRowDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Button, Card, Chip, Modal, Spinner, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { adminErrorText, personLabel } from '../../lib/format';
import { almatyTime } from '../../lib/purchase';
import { haptic } from '../../lib/tma';

const STATUSES: OrderStatus[] = ['paid', 'pending', 'failed', 'expired', 'refunded', 'chargeback'];

function Totals({ rows }: { rows: PaymentTotalsRowDto[] }) {
  const t = useT();
  const a = t.adminPay;
  const block = (period: 'today' | 'month', title: string) => {
    const mine = rows.filter((r) => r.period === period);
    return (
      <div>
        <div className="text-xs uppercase opacity-60 mb-1">{title}</div>
        {mine.length === 0 ? (
          <div className="text-sm text-hint">{a.noPayments}</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {mine.map((r) => (
              <li key={`${r.method}-${r.currency}`} className="flex justify-between gap-3">
                <span className="opacity-70">{t.purchase.methods[r.method]}</span>
                <span className="tabular-nums text-right">
                  {a.totalsLine(r.paidCount, formatAmount(r.paidSum, r.currency))}
                  {r.refundCount > 0 && (
                    <span className="block text-xs text-hint">{a.refundsLine(r.refundCount, formatAmount(r.refundSum, r.currency))}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  return (
    <Card variant="surface" padding="md" className="mb-4 space-y-3">
      {block('today', a.today)}
      {block('month', a.month)}
    </Card>
  );
}

/** «Оплаты» (spec 11.1): totals in each currency, the orders, and «Вернуть звёзды». */
export function AdminPaymentsPage() {
  const t = useT();
  const a = t.adminPay;
  const qc = useQueryClient();
  const [status, setStatus] = useState<OrderStatus | null>(null);
  // «Вернуть звёзды» for Stars, «Снять кредиты» for Kaspi and lava.top.
  const [refunding, setRefunding] = useState<AdminOrderDto | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const payments = useQuery({
    queryKey: ['admin', 'payments', status],
    queryFn: () => api.adminPayments(status ? { status } : {}),
  });

  const flash = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 4000);
  };

  const refund = useMutation({
    mutationFn: (o: AdminOrderDto) => (o.method === 'stars' ? api.adminRefundStars(o.code) : api.adminRevokeOrder(o.code)),
    onSuccess: (res) => {
      haptic('success');
      flash(a.refundStarsDone(res.taken, res.spent, res.credits));
    },
    // Telegram's own words when it refused: the owner sees why.
    onError: (err) => flash(err instanceof ApiError && err.code === 'TELEGRAM_ERROR' ? err.message : adminErrorText(err, t)),
    onSettled: () => {
      setRefunding(null);
      return qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
    },
  });

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={a.payments} to="/admin" />

      {payments.data && <Totals rows={payments.data.totals} />}

      <div className="flex flex-wrap gap-2 mb-3">
        <Chip selected={status === null} onClick={() => setStatus(null)}>
          {a.all}
        </Chip>
        {STATUSES.map((s) => (
          <Chip key={s} selected={status === s} onClick={() => setStatus(s)}>
            {a.statuses[s]}
          </Chip>
        ))}
      </div>

      {payments.isLoading && <Spinner size="sm" ariaLabel={t.common.loading} />}
      {payments.isError && <p className="text-sm text-hint">{t.common.failed}</p>}
      {payments.data && payments.data.orders.length === 0 && <p className="text-sm text-hint">{a.ordersEmpty}</p>}

      <ul className="space-y-2">
        {payments.data?.orders.map((o) => (
          <li key={o.id}>
            <Card variant="surface" padding="md" className="text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-bold tabular-nums">№{o.code}</span>
                <span className="text-xs text-hint tabular-nums">{almatyTime(o.paidAt ?? o.createdAt)}</span>
              </div>
              <div className="mt-1">
                {personLabel(o)} · «{o.packTitle}»
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="opacity-70">
                  {t.purchase.methods[o.method]} · {formatAmount(o.amount, o.currency)}
                </span>
                <span className="font-semibold">{a.statuses[o.status]}</span>
              </div>
              {o.status === 'paid' && (
                <Button variant="secondary" size="sm" className="mt-2" onClick={() => setRefunding(o)}>
                  {o.method === 'stars' ? a.refundStars : a.revoke}
                </Button>
              )}
            </Card>
          </li>
        ))}
      </ul>

      <Modal open={refunding !== null} onClose={() => setRefunding(null)}>
        {refunding && (
          <>
            <p className="text-sm mb-4">
              {(refunding.method === 'stars' ? a.refundStarsConfirm : a.revokeConfirm)(
                refunding.code,
                formatAmount(refunding.amount, refunding.currency),
              )}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => setRefunding(null)}>
                {a.cancel}
              </Button>
              <Button variant="destructive" loading={refund.isPending} disabled={refund.isPending} onClick={() => refund.mutate(refunding)}>
                {refunding.method === 'stars' ? a.refundStars : a.revoke}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {toast && (
        <Toast tone="neutral" placement="fixed">
          {toast}
        </Toast>
      )}
    </div>
  );
}
